/**
 * THE WORDS BACK FROM STRIPE'S PAGE: "Payments" over the heading, and the callout under it, as the
 * Payments artboard's third frame draws them, "Back in home"
 * (`faithfully-kept-business/design/home/Payments.dc.html`). Every line is the seam's, word for word
 * and in its order (ops's `docs/seams/home.md`, section 5, `RETURN_PAGE_WORDS`), each as the address
 * back says (`readReturn`, `lib/paying.ts`), and nothing of hers: no figure, no number, no name.
 *
 * Drawn in three places. Signed in, on the page Stripe sends her back to
 * (`screens/PaymentReturn.tsx`), above Payments; and signed in, at the top of an invoice's own page,
 * where the office's page and a page her texted link made come back to (`screens/Invoice.tsx`, P110).
 * Where a card with its Pay is drawn under them, after Stripe's back arrow, home's own line on how
 * to pay stands in section 5's `howToPay`'s place, "To pay, press Pay on the invoice below."
 * (`HOME_HOW_TO_PAY`, section 11.4 at ops 7e2d7ae, ops #71 item 4), since Pay is on the invoice under
 * it (`below` of `pay`). Where an invoice's own ways to pay are drawn under them, which open on
 * Zelle and draw no button named Pay, it is "To pay, choose a way to pay below."
 * (`INVOICE_HOW_TO_PAY`, `below` of `ways`; the fifth client-journey walk's review, finding 2). And
 * signed out, above the sign-in, at an invoice's own page or at the page Stripe sends her back to
 * (`screens/Door.tsx`; ops's plan for clients reaching only home, section 7, ask 3). There, on an
 * invoice's own page wherever its invoice is not drawn under them (on its way to her page, behind a
 * door that waits for her code, or not read: the review of fe216b8, finding 1) or is drawn with no
 * way to pay it, and on the page Stripe sends her back to where Payments cannot be read or draws no
 * card that offers Pay (the review of 574a196), no way to pay is below them (`below` of `nothing`),
 * so section 5's own line stands, "To pay, open your invoice again from the message we sent you.",
 * which is ops's reason for keeping it on a page with no button under it (11.4).
 */
import { HOME_HOW_TO_PAY, INVOICE_HOW_TO_PAY, PAYMENTS, RETURN_PAGE_WORDS } from '../words.ts';
import type { Back } from '../lib/paying.ts';
import { Phone } from './Phone.tsx';

const EYEBROW = 'font-mono text-small tracking-widest text-text-emphasis uppercase';

/**
 * What is drawn under the words: a card owed with its Pay (`pay`), an invoice's own ways to pay,
 * which draw no button named Pay (`ways`), or no way to pay at all (`nothing`).
 */
export type Below = 'pay' | 'ways' | 'nothing';

/** After Stripe's back arrow, the line on how to pay, by what is drawn under it. */
const HOW_TO_PAY: Readonly<Record<Below, string>> = {
  pay: HOME_HOW_TO_PAY,
  ways: INVOICE_HOW_TO_PAY,
  nothing: RETURN_PAGE_WORDS.howToPay,
};

/**
 * The lines the callout says, the first in bold, as the artboard draws it, in the seam's order; the
 * last, "Questions?", is drawn after them with the office's number. After Stripe's back arrow the
 * line on how to pay is home's own where Pay is below it (section 11.4), home's own for an
 * invoice's ways to pay where they are below it, and section 5's where no invoice and no way to pay
 * are below it (`Below`).
 */
export function returnLines(back: Back, below: Below): string[] {
  const words = RETURN_PAGE_WORDS;
  if (!back.paid) return [words.leftStripe, HOW_TO_PAY[below]];
  const lines: string[] = [words.onItsWay];
  // A card page takes no days, whatever the address says.
  if (back.page !== 'card') lines.push(words.bankTakesDays, words.confirmYourBank);
  // Her tick saves a bank account on the bank page alone.
  if (back.page === 'bank' && back.saves === 'account') lines.push(words.savedAccount);
  // Stripe's box, on the card page or the office's: the bank page never carries it.
  if (back.page !== 'bank' && back.saves === 'box') lines.push(words.savedBox);
  lines.push(words.close);
  return lines;
}

interface ReturnWordsProps {
  readonly back: Back;
  /**
   * What is drawn below them. `pay` above Payments with a card that offers Pay. `ways` above an
   * invoice's own page that draws its ways to pay. `nothing` above the sign-in, with no session; on
   * an invoice's own page that does not draw its invoice, or draws it with no way to pay it; on the
   * return page where Payments cannot be read; and above Payments drawn with no card that offers
   * Pay, as where nothing is owed or every invoice owed is on its way.
   */
  readonly below?: Below;
}

/** "Payments" over the heading, the page's `h1`, and the callout under it. */
export function ReturnWords({ back, below = 'pay' }: ReturnWordsProps) {
  const [first, ...rest] = returnLines(back, below);
  return (
    <>
      <div>
        <p className={EYEBROW}>{PAYMENTS.eyebrow}</p>
        <h1 className="mt-1 font-serif text-h2 font-semibold text-pretty text-text-body">
          {back.paid ? RETURN_PAGE_WORDS.paid : RETURN_PAGE_WORDS.notPaid}
        </h1>
      </div>
      <div className="flex gap-3 rounded-card border-2 border-border-brand bg-surface-accent-soft p-5">
        {back.paid ? <Checked /> : null}
        <div className="min-w-0">
          <p className="text-body font-semibold text-pretty text-text-body">{first}</p>
          {rest.map((line) => (
            <p key={line} className="mt-1 text-small text-pretty text-text-body">
              {line}
            </p>
          ))}
          <p className="mt-1 text-small text-pretty text-text-body">
            {RETURN_PAGE_WORDS.questionsBeforePhone} <Phone after="." />
          </p>
        </div>
      </div>
    </>
  );
}

/**
 * The artboard's check in a circle, drawn beside the thanks. The words say the same. How you pay
 * draws it beside ops's words that a way to pay is saved, back from a setup page (slice A5).
 */
export function Checked() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 28 28"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className="mt-0.5 shrink-0 text-text-brand"
    >
      <circle cx="14" cy="14" r="11.5" />
      <path d="M9 14.5l3.5 3.5L19 11" />
    </svg>
  );
}
